import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '@/lib/api';
import Seo from '@/components/Seo';
import InvoiceDocument from '@/components/InvoiceDocument';
import { applyBrand, resetBrand } from '@/lib/brand';
import { AlertTriangle, HardHat, Loader2, CheckCircle2, Banknote } from 'lucide-react';

const gbp = (n) => `£${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoicePublicPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, invoice: null, business: null });

  useEffect(() => {
    fetch(`${API}/public/invoices/${token}`, { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Not found'); return r.json(); })
      .then((d) => setState({ loading: false, error: null, ...d }))
      .catch((e) => setState({ loading: false, error: e.message, invoice: null, business: null }));
  }, [token]);

  useEffect(() => {
    if (state.business?.brand_color) applyBrand(state.business.brand_color);
    return () => resetBrand();
  }, [state.business]);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F4F4EF]"><Loader2 className="animate-spin text-slate-900" size={48} /></div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4EF] px-4">
        <div className="tm-card p-10 max-w-md text-center">
          <AlertTriangle className="mx-auto text-brand" size={48} />
          <h1 className="font-display font-semibold text-3xl uppercase tracking-tight mt-4">Invoice not found</h1>
          <p className="text-slate-600 mt-2">This invoice link may have expired or been withdrawn. Please contact the sender for a new one.</p>
        </div>
      </div>
    );
  }

  const { invoice, business } = state;
  const accent = business?.brand_color || '#FF5A1F';
  const isPaid = invoice.status === 'paid';
  const balance = invoice.balance_due != null ? invoice.balance_due : invoice.total;

  return (
    <div className="min-h-screen bg-[#F4F4EF]">
      <Seo title={`Invoice ${invoice.invoice_number}`} noindex />
      <div className="bg-slate-900 text-white border-b-2" style={{ borderColor: accent }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-semibold tracking-tight">
            <span className="px-2 py-1 inline-flex items-center gap-1" style={{ background: accent, color: '#fff' }}>
              <HardHat size={16} strokeWidth={2.5} />
              <span className="text-base">{business?.business_name || 'INVOICE'}</span>
            </span>
          </div>
          <span className="text-xs uppercase tracking-widest text-slate-300 font-semibold">Invoice {invoice.invoice_number}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {isPaid ? (
          <div className="tm-card p-6 mb-6 bg-emerald-50 border flex items-center gap-3" style={{ borderColor: '#059669' }} data-testid="invoice-paid-banner">
            <CheckCircle2 className="text-emerald-600 shrink-0" size={28} />
            <div>
              <h2 className="font-display font-semibold text-xl uppercase tracking-tight text-emerald-700">Paid — thank you</h2>
              <p className="text-slate-700 text-sm mt-1">This invoice has been marked as paid. No further action is needed.</p>
            </div>
          </div>
        ) : (
          <div className="tm-card p-6 mb-6" data-testid="invoice-pay-callout">
            <div className="flex items-center gap-3">
              <Banknote className="shrink-0" size={28} style={{ color: accent }} />
              <div className="flex-1">
                <h2 className="font-display font-semibold text-xl uppercase tracking-tight">Amount due: {gbp(balance)}</h2>
                <p className="text-slate-700 text-sm mt-1">
                  {invoice.payment_details ? 'Please pay using the details shown on the invoice below.' : 'Please contact the sender for payment details.'}
                  {(invoice.deposit_amount || 0) > 0 && invoice.amount_paid <= 0 && (
                    <> A deposit of <strong>{gbp(invoice.deposit_amount)}</strong> is requested to secure your booking.</>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="tm-card p-2 sm:p-4">
          <div className="pdf-preview-wrap">
            <InvoiceDocument invoice={invoice} business={business} />
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500 uppercase tracking-widest font-semibold">
          Powered by <a href="https://tradesmateuk.co.uk" target="_blank" rel="noreferrer" className="underline">TradesMate UK</a>
        </p>
      </div>
    </div>
  );
}
